import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Wishlist = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchWishlist = async () => {
      try {
        const response = await axios.get(`${API}/wishlist`);
        setProducts(response.data.products || []);
      } catch (error) {
        console.error('Failed to fetch wishlist:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, [isAuthenticated, navigate]);

  const handleRemove = async (productId, productName) => {
    try {
      await axios.delete(`${API}/wishlist/remove/${productId}`);
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.success(`${productName} removed from wishlist`);
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const handleAddToCart = async (product) => {
    const success = await addToCart(product.id);
    if (success) {
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#E8EFE9] rounded-3xl aspect-square skeleton"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-sm">
            <Heart className="w-16 h-16 text-[#6B7280] mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-[#1A3C34] font-['Chivo'] mb-4">
              Your wishlist is empty
            </h1>
            <p className="text-[#6B7280] mb-8">
              Save items you love by clicking the heart icon
            </p>
            <Link to="/products">
              <Button 
                className="rounded-full bg-[#D93052] text-white font-bold px-8 py-4 hover:bg-[#B02040]"
                data-testid="wishlist-shop-btn"
              >
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-[#1A3C34] font-['Chivo'] mb-8">
          My Wishlist
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <div 
              key={product.id}
              className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
              data-testid={`wishlist-item-${product.id}`}
            >
              <Link to={`/products/${product.id}`}>
                <div className="aspect-square overflow-hidden bg-[#E8EFE9]">
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>
              </Link>
              
              <div className="p-5">
                <Link to={`/products/${product.id}`}>
                  <h3 className="font-bold text-lg text-[#1A3C34] font-['Chivo'] mb-1 hover:text-[#D93052] transition-colors">
                    {product.name}
                  </h3>
                </Link>
                <p className="text-xl font-bold text-[#1A3C34] mb-4">
                  ${product.price.toFixed(2)}
                </p>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleAddToCart(product)}
                    className="flex-1 rounded-full bg-[#D93052] text-white font-bold hover:bg-[#B02040]"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Add to Cart
                  </Button>
                  <Button 
                    onClick={() => handleRemove(product.id, product.name)}
                    variant="outline"
                    className="rounded-full border-[#E8EFE9] hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                    data-testid={`remove-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
